/**
 * UserDetailPage Component
 *
 * Admin page for viewing and managing individual user details.
 */

import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { AdminLayout } from '../layout';
import { useUser, useUserActions, UserRole } from './hooks';
import {
  canSendRetrieveEmail,
  openRetrieveAccountEmail,
  SUPPORT_EMAIL,
} from './retrieveAccountEmail';
import { ChangeRoleModal, SuspendUserModal } from './components';

// Format date
const formatDate = (date: Date): string => {
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

// Format relative time
const formatRelativeTime = (date: Date | undefined): string => {
  if (!date) return 'Never';

  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins} minutes ago`;
  if (diffHours < 24) return `${diffHours} hours ago`;
  if (diffDays === 1) return 'yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  return formatDate(date);
};

export const UserDetailPage: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const { user, isLoading } = useUser(userId!);
  const { changeRole, suspendUser, unsuspendUser, sendPasswordResetLink, resendActivationEmail, isResendingActivation, isChangingRole, isSuspending, isUnsuspending } =
    useUserActions();

  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showSuspendModal, setShowSuspendModal] = useState(false);
  const [activeTab, setActiveTab] = useState<'activity' | 'bookings' | 'listings'>('activity');

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="animate-pulse space-y-6">
          <div className="bg-white rounded-xl p-6">
            <div className="flex items-center gap-4">
              <div className="w-20 h-20 bg-gray-200 rounded-full" />
              <div className="space-y-2">
                <div className="h-6 bg-gray-200 rounded w-48" />
                <div className="h-4 bg-gray-200 rounded w-32" />
              </div>
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (!user) {
    return (
      <AdminLayout>
        <div className="text-center py-12">
          <UserIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">User Not Found</h2>
          <p className="text-gray-500 mb-4">The user you're looking for doesn't exist.</p>
          <Link
            to="/admin/users"
            className="text-primary-500 hover:text-primary-600"
          >
            Back to Users
          </Link>
        </div>
      </AdminLayout>
    );
  }

  const roleColors: Record<string, string> = {
    admin: 'bg-purple-100 text-purple-800',
    host: 'bg-blue-100 text-blue-800',
    guest: 'bg-gray-100 text-gray-800',
  };

  const handleRoleChange = async (role: UserRole) => {
    return changeRole({ userId: user.id, role });
  };

  const handleSuspend = async (reason: string, duration: 'temporary' | 'permanent') => {
    return suspendUser({ userId: user.id, reason, duration });
  };

  const handleUnsuspend = async () => {
    await unsuspendUser(user.id);
  };

  const handleSendPasswordReset = async () => {
    const success = await sendPasswordResetLink(user.email);
    if (success) {
      toast.success('Password reset email sent');
    }
  };

  const handleResendActivation = async () => {
    try {
      await resendActivationEmail(user.email);
      toast.success(`Activation email sent to ${user.email}`);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to send activation email';
      toast.error(message);
    }
  };

  return (
    <AdminLayout>
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm">
          <Link to="/admin/users" className="text-gray-500 hover:text-gray-700">
            Users
          </Link>
          <ChevronRightIcon className="w-4 h-4 text-gray-400" />
          <span className="text-gray-900">{user.displayName}</span>
        </nav>

        {/* Header Card */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-20 h-20 rounded-full bg-gray-200 overflow-hidden flex-shrink-0">
                {user.photoURL ? (
                  <img
                    src={user.photoURL}
                    alt={user.displayName}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-primary-100 text-primary-600 text-2xl font-medium">
                    {user.displayName.charAt(0).toUpperCase()}
                  </div>
                )}
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{user.displayName}</h1>
                <p className="text-gray-500">{user.email}</p>
                <div className="flex items-center gap-2 mt-2">
                  <span
                    className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                      roleColors[user.role]
                    }`}
                  >
                    {user.role}
                  </span>
                  {user.isSuspended && (
                    <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-800">
                      Suspended
                    </span>
                  )}
                  {!user.emailVerified && (
                    <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">
                      Unverified
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setShowRoleModal(true)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Change Role
              </button>
              <button
                onClick={handleSendPasswordReset}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Reset Password
              </button>
              {/* Legacy hosts who never claimed their account. */}
              {canSendRetrieveEmail(user) && (
                <>
                  <button
                    onClick={handleResendActivation}
                    disabled={isResendingActivation}
                    className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50"
                    title="Sends the branded activation email so they can set a password and claim their account."
                  >
                    {isResendingActivation ? 'Sending…' : 'Resend activation email'}
                  </button>
                  <button
                    onClick={() => openRetrieveAccountEmail(user)}
                    className="px-4 py-2 border border-amber-300 bg-amber-50 text-amber-800 rounded-lg hover:bg-amber-100 transition-colors"
                    title={`Opens a draft in your mail app. Send it from ${SUPPORT_EMAIL}.`}
                  >
                    Draft a manual email
                  </button>
                </>
              )}
              {user.isSuspended ? (
                <button
                  onClick={handleUnsuspend}
                  disabled={isUnsuspending}
                  className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50"
                >
                  {isUnsuspending ? 'Unsuspending...' : 'Unsuspend'}
                </button>
              ) : (
                <button
                  onClick={() => setShowSuspendModal(true)}
                  className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                >
                  Suspend
                </button>
              )}
            </div>
          </div>

          {/* Suspension Info */}
          {user.isSuspended && (
            <div className="mt-4 p-4 bg-red-50 border border-red-100 rounded-lg">
              <div className="flex items-start gap-3">
                <BanIcon className="w-5 h-5 text-red-500 mt-0.5" />
                <div>
                  <p className="font-medium text-red-800">Account Suspended</p>
                  <p className="text-sm text-red-700 mt-1">
                    Reason: {user.suspensionReason || 'Not specified'}
                  </p>
                  {user.suspendedAt && (
                    <p className="text-sm text-red-600 mt-1">
                      Suspended on {formatDate(user.suspendedAt)}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Info Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Account Info */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="font-semibold text-gray-900 mb-4">Account Information</h2>
            <dl className="space-y-3">
              <div className="flex justify-between text-sm">
                <dt className="text-gray-500">User ID</dt>
                <dd className="font-mono text-gray-900">{user.id}</dd>
              </div>
              <div className="flex justify-between text-sm">
                <dt className="text-gray-500">Phone</dt>
                <dd className="text-gray-900">{user.phone || '-'}</dd>
              </div>
              <div className="flex justify-between text-sm">
                <dt className="text-gray-500">Registered</dt>
                <dd className="text-gray-900">{formatDate(user.createdAt)}</dd>
              </div>
              <div className="flex justify-between text-sm">
                <dt className="text-gray-500">Last Login</dt>
                <dd className="text-gray-900">{formatRelativeTime(user.lastLoginAt)}</dd>
              </div>
              <div className="flex justify-between text-sm">
                <dt className="text-gray-500">Email Verified</dt>
                <dd className="text-gray-900">{user.emailVerified ? 'Yes' : 'No'}</dd>
              </div>
            </dl>
          </div>

          {/* Statistics */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="font-semibold text-gray-900 mb-4">Statistics</h2>
            <dl className="space-y-3">
              {user.role === 'host' && (
                <>
                  <div className="flex justify-between text-sm">
                    <dt className="text-gray-500">Listings</dt>
                    <dd className="text-gray-900 font-medium">
                      {user.stats?.listingsCount || 0}
                    </dd>
                  </div>
                  <div className="flex justify-between text-sm">
                    <dt className="text-gray-500">Bookings Received</dt>
                    <dd className="text-gray-900 font-medium">
                      {user.stats?.bookingsReceived || 0}
                    </dd>
                  </div>
                  <div className="flex justify-between text-sm">
                    <dt className="text-gray-500">Total Earnings</dt>
                    <dd className="text-gray-900 font-medium">
                      ${user.stats?.totalEarnings?.toLocaleString() || 0}
                    </dd>
                  </div>
                </>
              )}
              <div className="flex justify-between text-sm">
                <dt className="text-gray-500">Bookings Made</dt>
                <dd className="text-gray-900 font-medium">
                  {user.stats?.bookingsMade || 0}
                </dd>
              </div>
              <div className="flex justify-between text-sm">
                <dt className="text-gray-500">Reviews Given</dt>
                <dd className="text-gray-900 font-medium">
                  {user.stats?.reviewsGiven || 0}
                </dd>
              </div>
            </dl>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="border-b border-gray-200">
            <nav className="flex">
              {user.role === 'host' && (
                <button
                  onClick={() => setActiveTab('listings')}
                  className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'listings'
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Listings
                </button>
              )}
              <button
                onClick={() => setActiveTab('bookings')}
                className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'bookings'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Bookings
              </button>
              <button
                onClick={() => setActiveTab('activity')}
                className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'activity'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Activity
              </button>
            </nav>
          </div>

          <div className="p-6">
            {activeTab === 'listings' && user.role === 'host' && (
              <UserListings userId={user.id} />
            )}
            {activeTab === 'bookings' && <UserBookings userId={user.id} />}
            {activeTab === 'activity' && <UserActivity userId={user.id} />}
          </div>
        </div>
      </div>

      {/* Modals */}
      <ChangeRoleModal
        isOpen={showRoleModal}
        onClose={() => setShowRoleModal(false)}
        user={user}
        onSave={handleRoleChange}
        isSaving={isChangingRole}
      />
      <SuspendUserModal
        isOpen={showSuspendModal}
        onClose={() => setShowSuspendModal(false)}
        user={user}
        onSuspend={handleSuspend}
        isSuspending={isSuspending}
      />
    </AdminLayout>
  );
};

// Placeholder components for tabs
const UserListings: React.FC<{ userId: string }> = ({ userId: _userId }) => (
  <div className="text-center py-8 text-gray-500">
    <BuildingIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
    <p>User's listings will appear here</p>
    <p className="text-sm mt-1">Connected to Firebase for real data</p>
  </div>
);

const UserBookings: React.FC<{ userId: string }> = ({ userId: _userId }) => (
  <div className="text-center py-8 text-gray-500">
    <CalendarIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
    <p>User's bookings will appear here</p>
    <p className="text-sm mt-1">Connected to Firebase for real data</p>
  </div>
);

const UserActivity: React.FC<{ userId: string }> = ({ userId: _userId }) => (
  <div className="text-center py-8 text-gray-500">
    <ClockIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
    <p>User's activity log will appear here</p>
    <p className="text-sm mt-1">Connected to Firebase for real data</p>
  </div>
);

// Icons
const UserIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
  </svg>
);

const ChevronRightIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
);

const BanIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
  </svg>
);

const BuildingIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
  </svg>
);

const CalendarIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

const ClockIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

export default UserDetailPage;
